/**
 * Project Page
 * Shows project details with map and asset list, with edit capability
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Tab,
  Tabs,
  Link as MuiLink,
} from '@mui/material';
import {
  ChevronLeft as BackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Inventory2 as InventoryIcon,
} from '@mui/icons-material';

import icnAssets from '../../assets/icons/icn_assets.svg';
import icnTag from '../../assets/icons/icn_tag.svg';
import icnError from '../../assets/icons/icn_error.svg';

import AppBar from '../elements/AppBar';
import FilterChip from '../FilterChip/FilterChip';
import AsseTracMap from '../elements/maps/AsseTracMap';
import LoadingOverlay from '../elements/LoadingOverlay';
import AnomalyDrawer from '../elements/AnomalyDrawer';
import MapOverlaysPanel from '../elements/MapOverlaysPanel';
import ProjectInventoryPanel from '../elements/ProjectInventoryPanel';
import ProjectYardSelector from '../elements/ProjectYardSelector';
import MovesTable from '../elements/MovesTable';
import AddInventoryDialog from '../elements/AddInventoryDialog';
import MapAreasDialog from '../elements/MapAreasDialog';
import MatLocationsDialog from '../elements/MatLocationsDialog';
import { structureBadges as toStructureBadges } from '../../utils/locationCounts';
import { fetchAssetTypes as fetchAssetTypesForCounts, fetchAssetSubTypes as fetchAssetSubTypesForCounts } from '../../store/slices/assetSlice';
import { fetchAreaElements } from '../../services/areaElementsApi';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { areaElementMarkers as toAreaElementMarkers, areaElementShapes as toAreaElementShapes, markersWithoutShapes, markersBounds, viewportForBounds } from '../../utils/areaElementLayer';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { WebMercatorViewport } from '@deck.gl/core';
import { fetchProject, setCurrentProject, updateProjectAction } from '../../store/slices/projectSlice';
import { fetchAssetsByProject } from '../../store/slices/assetSlice';
import {
  setAssetsData, setProjectBoundariesData, setOverlaysData, setMovesData,
  updateViewport, popViewportStack,
  setVisibleOverlays, toggleOverlay,
  saveProjectMapState, restoreProjectMapState, clearSavedProjectState,
} from '../../store/slices/mapSlice';
import { colorPalette, SPACING, FILTER_THRESHOLDS } from '../../theme';
import { formatDate, formatDateTime, safeGet } from '../../utils/helpers';
import * as api from '../../services/api';
import areasConfigData from '../../../data/areas-config.json';

const PANEL_WIDTH = 350;

const ProjectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isNewProject = id === 'new';
  
  const { currentProject, projectList, loading: projectLoading } = useAppSelector((state) => state.project);
  const { projectAssetList, loading: assetsLoading } = useAppSelector((state) => state.asset);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anomalyDrawerOpen, setAnomalyDrawerOpen] = useState(false);
  // ASS-74 item 5: for manual projects, start Overlays panel closed. The
  // previous useState(!isManualOrBothProject) initializer had two defects:
  // (a) it read currentProject on first render, before the project had
  // loaded (currentProject is null then, so the check always evaluated
  // false, regardless of the eventual trackingMode); (b) it scoped to
  // manual AND hybrid together, but the spec is manual only -- hybrid/
  // "Both" projects have beacons and must keep Overlays open. Default state
  // is open (safe for Beacon/Both while currentProject is still loading);
  // the effect below closes it once the loaded project is confirmed manual.
  const [overlaysPanelOpen, setOverlaysPanelOpen] = useState(true);
  const visibleOverlays = useAppSelector((state) => state.map.visibleOverlays);
  const savedProjectState = useAppSelector((state) => state.map.savedProjectState);
  const [projectAreas, setProjectAreas] = useState([]);
  
  // View tab state (0 = Map, 1 = Moves)
  const [activeViewTab, setActiveViewTab] = useState(0);

  // KMZ re-upload state for edit dialog
  const [editKmzData, setEditKmzData] = useState(null);
  const [editKmzLoading, setEditKmzLoading] = useState(false);

  // Toolbar filters (multi-select arrays; empty = all selected)
  const [filterAssetTypes, setFilterAssetTypes] = useState([]);
  const [filterOwners, setFilterOwners] = useState([]);
  const [filterBatteryIssues, setFilterBatteryIssues] = useState(false);
  const [filterOfflineGateways, setFilterOfflineGateways] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);

  // ASS-74 item 5 (cont'd): close Overlays once the loaded project is
  // confirmed manual; keep it open for Beacon/Both (including while still
  // loading). Keyed on id/trackingMode, not the whole currentProject object,
  // so unrelated project-field updates don't reopen/reclose the panel.
  useEffect(() => {
    if (currentProject) {
      setOverlaysPanelOpen(currentProject.trackingMode !== 'manual');
    }
  }, [currentProject?.id, currentProject?.trackingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ASS-74 item 4: for manual projects, hide the beacon-oriented filter
  // chips (Assets/Owner/Issues) -- they filter beacon-asset metadata that
  // manual-tracked projects don't have. Overlays stays available on Manual
  // projects regardless -- gated separately, further down.
  const isManualProject = currentProject?.trackingMode === 'manual';

  // ASS-72's re-declared derived value: needed by ASS-67's Add Inventory
  // CTA and ASS-76's View entries door, both of which show for manual OR
  // hybrid ("both") projects -- #133 removed this exact declaration when it
  // replaced the old useState(!isManualOrBothProject) initializer above, so
  // it's re-declared once here rather than duplicated at each call site.
  const isManualOrBothProject = currentProject?.trackingMode === 'manual' || currentProject?.trackingMode === 'hybrid';

  // ASS-67 item 5: Manual-only projects have no moves -- pin the view to
  // Map and hide the Moves tab entirely. Same value as isManualProject
  // (both mean trackingMode === 'manual'); kept as a separate name here
  // since #136's Moves-tab logic was written against isManualOnlyProject
  // and renaming call sites isn't worth the diff noise during this rebase.
  const isManualOnlyProject = isManualProject;
  const [addInventoryOpen, setAddInventoryOpen] = useState(false);
  // AST-88: See Mat Locations modal.
  const [matLocationsOpen, setMatLocationsOpen] = useState(false);
  // AST-87: reopen Map KMZ to Areas for a manual project.
  const [mapAreasOpen, setMapAreasOpen] = useState(false);

  // ASS-67 item 5 (cont'd): keep the view pinned to Map for manual-only
  // projects even if activeViewTab was somehow left at 1 (e.g. the project
  // switched from Both to Manual, or state.mode arrives after the initial
  // render).
  useEffect(() => {
    if (isManualOnlyProject && activeViewTab !== 0) {
      setActiveViewTab(0);
    }
  }, [isManualOnlyProject, activeViewTab]);

  // ASS-74: show a snackbar when arriving from the wizard. For manual/both
  // projects, include an 'Add inventory' action. ASS-82 follow-up: when the
  // location was geocoded on create, name the resolved place in the message
  // so the user can catch a wrong match immediately.
  useEffect(() => {
    if (location.state?.justCreated) {
      const mode = location.state.trackingMode;
      const isManualLike = mode === 'manual' || mode === 'hybrid';
      const geocodedPlaceName = location.state.geocodedPlaceName;
      let message;
      if (geocodedPlaceName) {
        message = `Project saved — located at ${geocodedPlaceName}.`;
      } else {
        message = isManualLike ? 'Project saved.' : 'Project created.';
      }
      // AST-87: the project exists even if its area mapping failed to save;
      // say so, and offer to map the areas again.
      const { areaSaveError, areaCount } = location.state;
      if (areaSaveError) {
        setSnackbar({ open: true, message: areaSaveError, severity: 'warning', action: 'mapAreas' });
      } else {
        if (areaCount > 0) message = `${message} ${areaCount} area element${areaCount === 1 ? '' : 's'} mapped.`;
        setSnackbar({
          open: true,
          message,
          severity: 'success',
          action: isManualLike ? 'addInventory' : null,
        });
      }
      // ASS-74 item 6: clear the state via React Router's own history API
      // instead of window.history.replaceState({}, ''), which wiped React
      // Router's own history.state out from under it (React Router keeps its
      // own state separate from the raw browser history entry) and could
      // break back-navigation/state elsewhere in the app.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps -- navigate is stable; location.pathname intentionally excluded to avoid re-firing this one-shot effect on route changes.

  // Fetch project-specific areas, fallback to global config if none mapped
  useEffect(() => {
    if (id && !isNewProject) {
      api.getProjectAreas(id)
        .then((result) => {
          const areas = result.items || [];
          if (areas.length > 0) {
            setProjectAreas(areas);
            // Feed areas with GeoJSON into the map overlay data
            const overlayData = {};
            for (const area of areas) {
              if (area.geoJson) {
                overlayData[area.id] = {
                  ...area,
                  areaTypeId: area.areaTypeId || area.id,
                  areaTypeName: area.name,
                };
              }
            }
            if (Object.keys(overlayData).length > 0) {
              dispatch(setOverlaysData(overlayData));
            }
          } else {
            setProjectAreas(areasConfigData.areaTypes);
          }
        })
        .catch(() => {
          setProjectAreas(areasConfigData.areaTypes);
        });
    }
  }, [id, isNewProject, dispatch]);

  const handleToggleOverlay = (overlayId) => {
    dispatch(toggleOverlay(overlayId));
  };

  const handleToggleAllOverlays = (checked) => {
    if (checked) {
      dispatch(setVisibleOverlays(projectAreas.map((a) => a.id)));
    } else {
      dispatch(setVisibleOverlays([]));
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    type: 'project',
    startDate: '',
    endDate: '',
  });

  // Load project data (skip if new)
  useEffect(() => {
    dispatch(setMovesData({}));

    if (id && !isNewProject) {
      // Always fetch project metadata (lightweight)
      dispatch(fetchProject(id));

      // ASS-81 Part B fix: only restore the saved snapshot if it belongs to
      // THIS project. The snapshot used to carry no project identity at
      // all, so opening project Y right after X incorrectly inherited X's
      // viewport/assets/boundary data -- restoreProjectMapState() ran
      // unconditionally whenever anything was saved, regardless of which
      // project it was saved for. A mismatched or stale snapshot is
      // discarded (clearSavedProjectState) rather than silently reused.
      if (savedProjectState && savedProjectState.projectId === id) {
        // Returning from asset detail on the SAME project -- restore map
        // state instead of re-fetching 1000+ assets.
        dispatch(restoreProjectMapState());
      } else {
        if (savedProjectState) dispatch(clearSavedProjectState());
        // Fresh navigation, or a different project than the snapshot --
        // fetch assets normally.
        dispatch(fetchAssetsByProject({ projectId: id }));
      }
    }

    // Don't clear currentProject on unmount - we want persistence across routes
  }, [dispatch, id, isNewProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form data when entering edit mode
  const handleEnterEditMode = useCallback(() => {
    if (currentProject) {
      setFormData({
        name: currentProject.name || '',
        description: currentProject.description || '',
        status: currentProject.status || 'active',
        type: currentProject.type || 'project',
        startDate: currentProject.startDate || '',
        endDate: currentProject.endDate || '',
      });
    }
    setEditMode(true);
  }, [currentProject]);

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData({
      name: '',
      description: '',
      status: 'active',
      type: 'project',
      startDate: '',
      endDate: '',
    });
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Handle back navigation - restore previous viewport
  const handleBackToProjects = () => {
    dispatch(popViewportStack());
    navigate('/');
  };

  const handleSaveProject = async () => {
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isNewProject) {
        // Create new project
        const projectData = {
          ...formData,
          customerId: activeCustomer?.id,
          createdAt: new Date().toISOString(),
        };
        
        const result = await api.createProject(projectData);
        navigate(`/projects/${result.item?.id || result.id}`);
      } else {
        const updateData = { ...formData };

        // Include KMZ data if a new file was uploaded
        if (editKmzData) {
          const { coordinatesToGeoJsonString } = await import('../../utils/kmzParser');
          const boundary = editKmzData.polygons.find(p =>
            p.name.toLowerCase().includes('boundary') || p.name.toLowerCase().includes('project')
          ) || editKmzData.polygons[0];

          if (boundary?.geometry?.coordinates?.[0]) {
            updateData.boundaries = coordinatesToGeoJsonString(boundary.geometry.coordinates[0]);
          }
          updateData.kmzFileName = editKmzData.fileName;

          // Build areaMappings from all non-boundary features grouped by geometry type
          const features = editKmzData.features.filter(f => f.id !== boundary?.id);
          if (features.length > 0) {
            updateData.areaMappings = features.map(f => ({
              areaTypeId: f.id,
              associations: [{ customName: f.name, feature: f }],
            }));
          }
        }

        // R-22: if the project was Draft and now has a boundary (either the
        // existing one on the record or a new one uploaded in this edit),
        // transition status off Draft. Do not silently upgrade if the user
        // explicitly picked a status via the Status field — respect their
        // choice.
        const hadBoundaryBefore = !!currentProject.boundaries;
        const hasBoundaryNow = !!updateData.boundaries || hadBoundaryBefore;
        if (currentProject.status === 'draft' && hasBoundaryNow && formData.status === 'draft') {
          updateData.status = 'active';
        }

        await dispatch(updateProjectAction({ id: currentProject.id, data: updateData })).unwrap();
        setSnackbar({ open: true, message: 'Project updated successfully', severity: 'success' });
        setEditMode(false);
        setEditKmzData(null);
        dispatch(fetchProject(id));
      }
    } catch (err) {
      setError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  // Track whether we restored from snapshot (skip zoom-to-fit in that case)
  const [restoredFromSnapshot, setRestoredFromSnapshot] = useState(false);

  // Update the fetch effect to flag restoration. ASS-81 Part B fix: only
  // flag it when the snapshot actually belongs to THIS project -- this ran
  // on savedProjectState truthiness alone before, so opening project Y
  // right after saving a snapshot for X would skip Y's zoom-to-fit too.
  //
  // Second fix in the same spot: this effect used [] deps, so it only ever
  // ran on the FIRST mount of this component for the whole session.
  // React Router does not remount ProjectPage when navigating between two
  // /projects/:id routes -- it's the same component instance with a new
  // id param -- so a second "return to the same project" within one
  // session could never re-flag restoration, and the zoom-to-fit/point
  // effects below would always override a legitimately-matched restore.
  // Keyed on id so it re-evaluates on every project navigation, not just
  // the session's first one.
  useEffect(() => {
    if (savedProjectState && savedProjectState.projectId === id) {
      setRestoredFromSnapshot(true);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update map data when project loads + zoom to fit
  useEffect(() => {
    if (currentProject) {
      if (currentProject.boundaries) {
        dispatch(setProjectBoundariesData({
          [currentProject.id]: {
            id: currentProject.id,
            boundaries: currentProject.boundaries,
            name: currentProject.name,
          },
        }));

        // Skip zoom-to-fit if we just restored from a saved snapshot
        if (restoredFromSnapshot) {
          setRestoredFromSnapshot(false);
          return;
        }

        // Zoom to fit the project boundary
        let coords = [];
        try {
          if (typeof currentProject.boundaries === 'string') {
            const geo = JSON.parse(currentProject.boundaries);
            if (geo.coordinates?.[0]) coords = geo.coordinates[0];
          } else if (Array.isArray(currentProject.boundaries)) {
            coords = currentProject.boundaries.map(b => [b.longitude, b.latitude]);
          }
        } catch { /* ignore */ }

        if (coords.length > 0) {
          let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
          for (const c of coords) {
            const lng = c[0], lat = c[1];
            if (lat != null && lng != null) {
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
            }
          }
          if (minLat !== Infinity) {
            const latDelta = maxLat - minLat;
            const lngDelta = maxLng - minLng;
            const maxDelta = Math.max(latDelta, lngDelta);
            let zoom = 15;
            if (maxDelta > 2) zoom = 7;
            else if (maxDelta > 1) zoom = 8;
            else if (maxDelta > 0.5) zoom = 9;
            else if (maxDelta > 0.2) zoom = 10;
            else if (maxDelta > 0.1) zoom = 11;
            else if (maxDelta > 0.05) zoom = 12;
            else if (maxDelta > 0.02) zoom = 13;
            else if (maxDelta > 0.01) zoom = 14;

            dispatch(updateViewport({
              latitude: (minLat + maxLat) / 2,
              longitude: (minLng + maxLng) / 2,
              zoom,
            }));
          }
        }
      }
    }
  }, [dispatch, currentProject]);

  // ASS-81 Part B: manual (or Both, if it somehow has no boundary but a
  // location) projects with a lat/long and no boundary shape -- centre on
  // that point at zoom 14 and show its marker via projectBoundariesData's
  // location-only shape (same shape ASS-81 Part A's useMapLayers.js reads).
  // Mutually exclusive with the boundary branch above: a project is either
  // boundary-rendered or point-rendered, never both.
  const projectLat = currentProject?.location?.lat;
  const projectLng = currentProject?.location?.lng;
  const hasValidProjectPoint = typeof projectLat === 'number' && typeof projectLng === 'number'
    && !Number.isNaN(projectLat) && !Number.isNaN(projectLng);
  useEffect(() => {
    if (!currentProject || currentProject.boundaries) return;
    if (!hasValidProjectPoint) return;

    dispatch(setProjectBoundariesData({
      [currentProject.id]: {
        id: currentProject.id,
        name: currentProject.name,
        location: { lat: projectLat, lng: projectLng },
      },
    }));

    if (restoredFromSnapshot) {
      setRestoredFromSnapshot(false);
      return;
    }

    dispatch(updateViewport({ latitude: projectLat, longitude: projectLng, zoom: 14 }));
  }, [dispatch, currentProject, hasValidProjectPoint, projectLat, projectLng]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapViewport = useAppSelector((state) => state.map.viewport);

  // AST-96: draw a manual project's mapped KMZ elements on its map. Fetched
  // per project; refetched after Map Areas saves (areaElementsVersion). A
  // failed load is non-fatal -- the map still shows the project point -- but
  // it's surfaced so an empty map isn't mistaken for "nothing mapped".
  const [areaElements, setAreaElements] = useState([]);
  const [areaElementsVersion, setAreaElementsVersion] = useState(0);
  const [areaElementsError, setAreaElementsError] = useState(null);
  const areaProjectId = isManualProject ? currentProject?.id : null;
  useEffect(() => {
    setAreaElements([]);
    setAreaElementsError(null);
    if (!areaProjectId) return undefined;
    let cancelled = false;
    fetchAreaElements(areaProjectId)
      .then((res) => { if (!cancelled) setAreaElements(res?.items || []); })
      .catch(() => { if (!cancelled) setAreaElementsError("Couldn't load this project's mapped areas."); });
    return () => { cancelled = true; };
  }, [areaProjectId, areaElementsVersion]);

  // AST-124 MA: with the Mapped areas legend gone, a failed load would be
  // invisible -- and an empty map would read as "nothing mapped". Say so.
  useEffect(() => {
    if (areaElementsError) setSnackbar({ open: true, message: areaElementsError, severity: 'warning' });
  }, [areaElementsError]);

  // AST-125 MP: the project customer's manual assets (archived included, so
  // past counts are still named), for the pill popups. Same project-scoped
  // read the scoreboard and Enter Asset Move use. Non-fatal: on failure the
  // popups fall back to subtype names, exactly as before.
  const [projectManualAssets, setProjectManualAssets] = useState([]);
  const manualAssetsProjectId = isManualOrBothProject ? currentProject?.id : null;
  useEffect(() => {
    setProjectManualAssets([]);
    if (!manualAssetsProjectId) return undefined;
    let cancelled = false;
    fetchProjectManualAssets(manualAssetsProjectId)
      .then((res) => { if (!cancelled) setProjectManualAssets(res?.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [manualAssetsProjectId]);

  // AST-89: green count pills on structures, from the same entries the
  // MAT (manual) row reads (ProjectInventoryPanel loads them) and the Mat
  // Types the Add Inventory dialog offers. On by default for manual
  // projects; toggled from Map Overlays; reset when the project changes.
  const projectEntries = useAppSelector((state) => (currentProject?.id ? state.count.entriesByProject[currentProject.id] : null));
  const { assetTypes: assetTypesList, assetSubTypes: assetSubTypesList } = useAppSelector((state) => state.asset);
  useEffect(() => {
    if (!isManualOrBothProject) return;
    if (!assetTypesList || assetTypesList.length === 0) dispatch(fetchAssetTypesForCounts());
    if (!assetSubTypesList || assetSubTypesList.length === 0) dispatch(fetchAssetSubTypesForCounts());
  }, [isManualOrBothProject]); // eslint-disable-line react-hooks/exhaustive-deps
  const matTypesForCounts = useMemo(() => {
    const mat = (assetTypesList || []).find((t) => t.name === 'Mat');
    if (!mat) return [];
    return (assetSubTypesList || []).filter((s) => s.typeId === mat.id).map((s) => ({ id: s.id, name: s.name }));
  }, [assetTypesList, assetSubTypesList]);
  const [showManualCounts, setShowManualCounts] = useState(true);
  useEffect(() => { setShowManualCounts(true); }, [currentProject?.id]);
  // AST-111 follow-up: where the project-level pill is anchored.
  //
  // Mark asked for it "adjacent to project marker", and my first pass used
  // currentProject.location blindly. That was wrong in two ways, both seen on
  // live dev:
  //
  //  1. Boundary projects render with showProjectMarkers=false, so there is
  //     no hexagon marker on the page -- "adjacent to the project marker"
  //     anchored the pill to something that isn't drawn.
  //  2. A project's stored point can be far from its mapped elements. On
  //     "Just Another" the pill landed at pixel (15865, 3958) on a 1728x769
  //     canvas -- in the DOM, invisible on screen. Exactly the "attached to
  //     the project but doesn't show on the map" Mark reported.
  //
  // So: use the project point when it's actually within the mapped elements'
  // extent (the manual-project case Mark described, where the hexagon is
  // drawn), otherwise fall back to the centre of those elements -- which is
  // what the map fits to, so the pill is always where the user is looking.
  // NOTE: areaMarkers/areaShapes are declared here, above projectPillPoint,
  // because that useMemo reads them. `const` is not hoisted -- having them
  // below threw "Cannot access 'areaMarkers' before initialization" on first
  // render, which blanked the whole page (caught on dev, see PR #201).
  const areaMarkers = useMemo(() => toAreaElementMarkers(areaElements), [areaElements]);
  const areaShapes = useMemo(() => toAreaElementShapes(areaElements), [areaElements]);

  const projectPillPoint = useMemo(() => {
    const bounds = markersBounds(areaMarkers, areaShapes);
    const pointInside = hasValidProjectPoint && bounds
      && projectLng >= bounds[0][0] && projectLng <= bounds[1][0]
      && projectLat >= bounds[0][1] && projectLat <= bounds[1][1];
    if (pointInside) return { lat: projectLat, lng: projectLng };
    if (bounds) {
      return {
        lat: (bounds[0][1] + bounds[1][1]) / 2,
        lng: (bounds[0][0] + bounds[1][0]) / 2,
      };
    }
    // No elements mapped at all: the map is centred on the project point, so
    // that's the right anchor. No point either -> no pill.
    return hasValidProjectPoint ? { lat: projectLat, lng: projectLng } : null;
  }, [areaMarkers, areaShapes, hasValidProjectPoint, projectLat, projectLng]);

  const structureBadges = useMemo(
    // isManualOrBothProject, not isManualProject: hybrid ("Both") projects
    // carry manual mats too -- they have Add Inventory and See Mat Locations
    // -- and the first cut of AST-111 silently drew no pills for them.
    () => (isManualOrBothProject && showManualCounts
      ? toStructureBadges(
        projectEntries || [],
        areaElements,
        matTypesForCounts,
        projectPillPoint,
        projectManualAssets // AST-125 MP: popup rows by manual asset
      )
      : []),
    [isManualOrBothProject, showManualCounts, projectEntries, areaElements, matTypesForCounts, projectPillPoint, projectManualAssets]
  );

  // All active elements (for the legend counts and "has anything mapped"),
  // their shapes, and the dots to draw: points plus anything with no stored
  // shape -- a yard drawn as an outline doesn't also get a dot in its middle.
  const areaDots = useMemo(() => markersWithoutShapes(areaMarkers, areaElements), [areaMarkers, areaElements]);
  const hasAreaMarkers = areaMarkers.length > 0;

  // No project point but mapped elements: fit the map to the elements.
  // Only on the first load per project -- not after every Map Areas save,
  // which would yank the view away from wherever the user had panned.
  const fittedAreasForRef = useRef(null);
  useEffect(() => {
    if (!isManualProject || hasValidProjectPoint || !hasAreaMarkers) return;
    if (fittedAreasForRef.current === currentProject?.id) return;
    if (restoredFromSnapshot) return;
    const vp = viewportForBounds(markersBounds(areaMarkers, areaShapes), {
      width: mapViewport?.width,
      height: mapViewport?.height,
      WebMercatorViewport,
    });
    if (vp) {
      fittedAreasForRef.current = currentProject?.id;
      dispatch(updateViewport(vp));
    }
  }, [isManualProject, hasValidProjectPoint, hasAreaMarkers, areaMarkers, areaShapes, currentProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to normalize asset coordinates
  const getAssetCoordinates = (asset) => {
    if (asset.coordinate) {
      return asset.coordinate;
    }
    if (asset.location && asset.location.lat != null && asset.location.lng != null) {
      return {
        latitude: asset.location.lat,
        longitude: asset.location.lng,
      };
    }
    return null;
  };

  // Build asset type lookup for resolving type names
  // (assetTypesList is declared above, with the AST-89 badge inputs)
  const assetTypeMap = useMemo(() => {
    const map = {};
    for (const t of (assetTypesList || [])) map[t.id] = t;
    return map;
  }, [assetTypesList]);

  // Unique asset types and owners for filter dropdowns
  const uniqueAssetTypes = useMemo(() => {
    const types = new Set();
    for (const a of projectAssetList) {
      const t = a.type?.name || assetTypeMap[a.assetTypeId]?.name || a.assetTypeName;
      if (t) types.add(t);
    }
    return [...types].sort();
  }, [projectAssetList, assetTypeMap]);

  const uniqueOwners = useMemo(() => {
    const owners = new Set();
    for (const a of projectAssetList) {
      const o = a.owner || a.ownerName;
      if (o) owners.add(o);
    }
    return [...owners].sort();
  }, [projectAssetList]);

  // Update map asset data (with filters applied)
  useEffect(() => {
    if (projectAssetList.length === 0) return;

    const assetsData = {};
    let count = 0;
    
    for (const asset of projectAssetList) {
      let coordinates = null;
      if (asset.coordinate?.latitude != null && asset.coordinate?.longitude != null) {
        coordinates = asset.coordinate;
      } else if (asset.location?.lat != null && asset.location?.lng != null) {
        coordinates = { latitude: asset.location.lat, longitude: asset.location.lng };
      } else if (asset.movedCoordinate?.latitude != null) {
        coordinates = asset.movedCoordinate;
      }
      if (!coordinates) continue;

      const resolvedType = asset.type || assetTypeMap[asset.assetTypeId] || {};
      const assetTypeName = resolvedType.name || asset.assetTypeName || '';

      // Apply filters
      if (filterAssetTypes.length > 0 && !filterAssetTypes.includes(assetTypeName)) continue;
      if (filterOwners.length > 0 && !filterOwners.includes(asset.owner || asset.ownerName || '')) continue;
      if (filterBatteryIssues) {
        const batt = asset.battery || asset.beaconBattery;
        if (batt == null || batt >= FILTER_THRESHOLDS.BATTERY_ISSUE_PERCENT) continue;
      }
      if (filterOfflineGateways) {
        const devType = asset.deviceType || resolvedType.deviceType || '';
        if (devType.toLowerCase() !== 'gateway') continue;
      }
      
      assetsData[asset.id] = {
        id: asset.id,
        unitNumber: safeGet('unitNumber', asset),
        name: asset.name,
        type: resolvedType,
        assetTypeName,
        subType: asset.subType,
        coordinates,
        status: asset.status || asset.movingStatus || 'idle',
        movingStatus: asset.movingStatus,
        condition: asset.condition,
        rotationRequired: asset.rotationRequired,
        battery: asset.battery || asset.beaconBattery,
        lastReceived: formatDateTime(safeGet('receivedTs', asset), 'M/D/YY h:mmA'),
        cleanStatus: asset.cleanStatus,
      };
      count++;
    }

    dispatch(setAssetsData(assetsData));
  }, [dispatch, projectAssetList, assetTypeMap, filterAssetTypes, filterOwners, filterBatteryIssues, filterOfflineGateways]);

  // Apply toolbar filters to the asset list for both map and inventory
  const filteredAssetList = useMemo(() => {
    return projectAssetList.filter((asset) => {
      const resolvedType = asset.type || assetTypeMap[asset.assetTypeId] || {};
      const typeName = resolvedType.name || asset.assetTypeName || '';
      if (filterAssetTypes.length > 0 && !filterAssetTypes.includes(typeName)) return false;
      const ownerName = asset.owner || asset.ownerName || '';
      if (filterOwners.length > 0 && !filterOwners.includes(ownerName)) return false;
      if (filterBatteryIssues) {
        const batt = asset.battery || asset.beaconBattery;
        if (batt == null || batt >= FILTER_THRESHOLDS.BATTERY_ISSUE_PERCENT) return false;
      }
      if (filterOfflineGateways) {
        const devType = asset.deviceType || resolvedType.deviceType || '';
        if (devType.toLowerCase() !== 'gateway') return false;
      }
      return true;
    });
  }, [projectAssetList, assetTypeMap, filterAssetTypes, filterOwners, filterBatteryIssues, filterOfflineGateways]);

  // Viewport-based asset filtering for inventory panel (mapViewport is
  // declared above, with the AST-96 area elements, which also need it)
  const viewportAssets = useMemo(() => {
    if (!mapViewport?.width || !mapViewport?.height || mapViewport.zoom < 2) return filteredAssetList;
    try {
      const vp = new WebMercatorViewport({
        width: mapViewport.width || 800,
        height: mapViewport.height || 600,
        latitude: mapViewport.latitude,
        longitude: mapViewport.longitude,
        zoom: mapViewport.zoom,
      });
      const [minLng, minLat] = vp.unproject([0, mapViewport.height || 600]);
      const [maxLng, maxLat] = vp.unproject([mapViewport.width || 800, 0]);
      return filteredAssetList.filter((a) => {
        const coord = a.coordinate || a.location || a.movedCoordinate;
        if (!coord) return false;
        const lat = coord.latitude ?? coord.lat;
        const lng = coord.longitude ?? coord.lng;
        if (lat == null || lng == null) return false;
        return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
      });
    } catch {
      return filteredAssetList;
    }
  }, [filteredAssetList, mapViewport?.latitude, mapViewport?.longitude, mapViewport?.zoom, mapViewport?.width, mapViewport?.height]);

  // Create project selector for AppBar. On a project's own page it shows that
  // project (AST-125 PS-4); choosing "Projects" opens Home (PS-3, AC-2) --
  // it used to do nothing here.
  const ProjectSelectorComponent = useMemo(() => (
    <ProjectYardSelector
      projects={projectList}
      selectedProjectId={currentProject?.id || null}
      onProjectSelect={(projectId) => {
        if (!projectId) {
          dispatch(setCurrentProject(null));
          navigate('/');
        } else if (projectId !== currentProject?.id) {
          navigate(`/projects/${projectId}`);
        }
      }}
      placeholder="Projects"
    />
  ), [projectList, currentProject?.id, navigate, dispatch]);

  // New Project Form
  if (isNewProject) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar
          showProjectSelector
          projectSelectorProps={{
            ProjectSelectorComponent,
          }}
        />
        <Box
          sx={{
            maxWidth: { xs: '94vw', sm: '88vw', md: '88vw' },
            width: '100%',
            margin: '0 auto',
            pt: '40px',
            px: '24px',
            pb: '8px',
          }}
        >
          <Button
            variant="text"
            color="secondary"
            startIcon={<BackIcon />}
            onClick={() => navigate('/projects')}
            sx={{ mb: 3, height: 44 }}
          >
            Back to Projects
          </Button>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: '24px' }}
          >
            Create New Project
          </Typography>
              
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Project Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                fullWidth
                required
                autoFocus
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleFormChange('description')}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Type"
                value={formData.type}
                onChange={handleFormChange('type')}
                select
                fullWidth
              >
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="yard">Yard</MenuItem>
                <MenuItem value="site">Site</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Status"
                value={formData.status}
                onChange={handleFormChange('status')}
                select
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={handleFormChange('startDate')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={handleFormChange('endDate')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/projects')}
              sx={{ height: 44 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSaveProject}
              disabled={saving}
              sx={{ height: 44 }}
            >
              {saving ? 'Creating...' : 'Create Project'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (projectLoading && !currentProject) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar
          showProjectSelector
          projectSelectorProps={{
            ProjectSelectorComponent,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar
          showProjectSelector
          projectSelectorProps={{
            ProjectSelectorComponent,
          }}
        />
        <Box sx={{ py: 4, textAlign: 'center', maxWidth: { xs: '94vw', sm: '88vw' }, margin: '0 auto', px: 3 }}>
          <Typography variant="h5" color="text.secondary">
            Project not found
          </Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBackToProjects}
            sx={{ mt: 2, height: 44 }}
          >
            Back to Map
          </Button>
        </Box>
      </Box>
    );
  }

  // Panel content - reused for both desktop sidebar and mobile drawer
  const PanelContent = () => (
    <>
      {/* Back Button */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          variant="text"
          color="secondary"
          startIcon={<BackIcon />}
          onClick={handleBackToProjects}
          size="small"
        >
          Back to Map
        </Button>
        {isMobile && (
          <IconButton onClick={() => setInfoPanelOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* Project toolbar */}
      {!editMode && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {/* ASS-67: PanelContent's own "Add Inventory" button (routing to
              /manual/inventory) has been removed -- #128 shipped it inside
              this panel, which is dead: PanelContent is defined but never
              rendered anywhere in this component (confirmed by search).
              The real CTA now lives in the visible Map|Moves toolbar,
              below, gated on the PROJECT's tracking mode. */}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEnterEditMode}
            size="small"
          >
            Edit Project
          </Button>
        </Box>
      )}

      {/* Project Details / Edit Form */}
      <Box sx={{ p: 2 }}>
        {editMode ? (
          /* Edit Form */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            <TextField
              label="Project Name"
              value={formData.name}
              onChange={handleFormChange('name')}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleFormChange('description')}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            <TextField
              label="Type"
              value={formData.type}
              onChange={handleFormChange('type')}
              select
              fullWidth
              size="small"
            >
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="yard">Yard</MenuItem>
              <MenuItem value="site">Site</MenuItem>
            </TextField>

            <TextField
              label="Status"
              value={formData.status}
              onChange={handleFormChange('status')}
              select
              fullWidth
              size="small"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>

            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={handleFormChange('startDate')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={handleFormChange('endDate')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* KMZ Re-upload */}
            <Box sx={{ mt: 1, p: 2, border: `1px dashed ${colorPalette.lightSteel}`, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Upload New KMZ/KML File (optional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                Browse KMZ/KML...
                <input
                  type="file"
                  accept=".kmz,.kml"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { parseKmzFile } = await import('../../utils/kmzParser');
                      const parsed = await parseKmzFile(file);
                      // Update project boundaries with the new file
                      const boundary = parsed.polygons.find(p =>
                        p.name.toLowerCase().includes('boundary') || p.name.toLowerCase().includes('project')
                      ) || parsed.polygons[0];

                      if (boundary) {
                        const { coordinatesToGeoJsonString } = await import('../../utils/kmzParser');
                        const boundaryCoords = boundary.geometry?.coordinates?.[0];
                        const geoJsonStr = boundaryCoords ? coordinatesToGeoJsonString(boundaryCoords) : null;
                        if (geoJsonStr) {
                          await api.updateProject(id, {
                            boundaries: geoJsonStr,
                            kmzFileName: file.name,
                          });
                          setSnackbar({ open: true, message: `KMZ updated: ${parsed.featureCount} features loaded`, severity: 'success' });
                          dispatch(fetchProject(id));
                        }
                      }
                    } catch (err) {
                      setSnackbar({ open: true, message: `KMZ error: ${err.message}`, severity: 'error' });
                    }
                  }}
                />
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveProject}
                disabled={saving}
                size="small"
                sx={{ backgroundColor: colorPalette.asseTracOrange }}
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          /* View Mode */
          <>
            <Typography variant="h5" gutterBottom>
              {currentProject.name}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={currentProject.status}
                size="small"
                color={currentProject.status === 'active' ? 'success' : 'default'}
              />
              {currentProject.type && (
                <Chip label={currentProject.type} size="small" variant="outlined" />
              )}
            </Box>

            {currentProject.client && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Client: {currentProject.client}
              </Typography>
            )}

            {currentProject.description && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {currentProject.description}
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              {currentProject.startDate && (
                <Typography variant="body2" color="text.secondary">
                  Start: {formatDate(currentProject.startDate)}
                </Typography>
              )}
              {currentProject.endDate && (
                <Typography variant="body2" color="text.secondary">
                  End: {formatDate(currentProject.endDate)}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>

      <Divider />

      {/* Asset Summary */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Assets ({projectAssetList.length})
        </Typography>
        
        {projectAssetList.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assets assigned to this project
          </Typography>
        ) : (
          <Box sx={{ maxHeight: isMobile ? '50vh' : 300, overflow: 'auto' }}>
            {projectAssetList.slice(0, 20).map((asset) => (
              <Box
                key={asset.id}
                sx={{
                  py: 1,
                  px: 2,
                  mb: 1,
                  borderRadius: 1,
                  backgroundColor: 'background.default',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => {
                  if (isMobile) setInfoPanelOpen(false);
                  navigate(`/asset/${asset.id}`);
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {asset.name || asset.unitNumber || 'Unnamed'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {asset.type?.name}
                </Typography>
              </Box>
            ))}
            {projectAssetList.length > 20 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                +{projectAssetList.length - 20} more assets
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />

      {/* Edit Project Dialog */}
      <Dialog open={editMode} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
            )}
            <TextField
              label="Project Name"
              value={formData.name}
              onChange={handleFormChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleFormChange('description')}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Type"
                value={formData.type}
                onChange={handleFormChange('type')}
                select
                fullWidth
              >
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="yard">Yard</MenuItem>
                <MenuItem value="site">Site</MenuItem>
              </TextField>
              <TextField
                label="Status"
                value={formData.status}
                onChange={handleFormChange('status')}
                select
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={handleFormChange('startDate')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={handleFormChange('endDate')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Divider sx={{ mt: 1 }} />

            {/* KMZ Re-Upload */}
            <Box sx={{ mt: 1, p: 2, border: `1px dashed ${colorPalette.lightSteel}`, borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                Upload New KMZ/KML (optional)
              </Typography>
              {editKmzData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {editKmzData.fileName} ({editKmzData.featureCount} features)
                  </Typography>
                  <Button size="small" onClick={() => setEditKmzData(null)}>Remove</Button>
                </Box>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={editKmzLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {editKmzLoading ? <CircularProgress size={16} /> : 'Browse KMZ/KML...'}
                  <input
                    type="file"
                    accept=".kmz,.kml"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setEditKmzLoading(true);
                      try {
                        const { parseKmzFile } = await import('../../utils/kmzParser');
                        const parsed = await parseKmzFile(file);
                        setEditKmzData(parsed);
                      } catch (err) {
                        setSnackbar({ open: true, message: `KMZ error: ${err.message}`, severity: 'error' });
                      } finally {
                        setEditKmzLoading(false);
                      }
                    }}
                  />
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { handleCancelEdit(); setEditKmzData(null); }} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveProject}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ backgroundColor: colorPalette.asseTracOrange }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toolbar: Map/Moves tabs + Project filters */}
      <Box
        component="nav"
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${colorPalette.lightSteel}`,
          backgroundColor: '#3A3938',
          height: 52,
          minHeight: 52,
          flexShrink: 0,
          // Match top app bar padding: 8px mobile, 24px desktop
          px: { xs: '8px', sm: '24px' },
          gap: 1,
          flexWrap: 'nowrap',
          overflowX: 'auto',
        }}
      >
        {/* Map and Moves Tabs. ASS-67 item 5: Manual-only projects have no
            moves -- "No moves yet for manual tracking, just inventory
            counts" (Mark, ASS-42). Hide the Moves tab entirely rather than
            leaving it clickable-but-empty, and keep the view pinned to Map.
            Both/hybrid projects have beacons and keep Moves exactly as
            today. */}
        <Tabs
          value={activeViewTab}
          onChange={(_, newValue) => setActiveViewTab(newValue)}
          sx={{
            minHeight: 52,
            '& .MuiTabs-indicator': {
              backgroundColor: colorPalette.asseTracOrange,
              height: 3,
            },
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#A09C9A',
              '&.Mui-selected': {
                color: '#FFFFFF',
              },
            },
          }}
        >
          <Tab label="Map" />
          {!isManualOnlyProject && <Tab label="Moves" />}
        </Tabs>

        {/* Filter chips and divider - only show on Map tab. ASS-74 item 4:
            also hidden for Manual projects -- these chips filter
            beacon-asset metadata (asset type, owner, battery/gateway
            issues) that manual-tracked projects don't have. Overlays stays
            available on Manual projects regardless (rendered further down,
            outside this block). */}
        {/* ASS-67 item 2: Add Inventory door, visible in the toolbar
            regardless of which tab is active, gated on the PROJECT's
            tracking mode (manual or hybrid/"both") -- not customer
            capability, since a hybrid customer can still have a
            beacon-only project. Supersedes #128's dead PanelContent
            button (removed above) and the superseded /manual/inventory
            route. */}
        {isManualOrBothProject && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
            <Button
              variant="contained"
              size="small"
              startIcon={<InventoryIcon />}
              onClick={() => setAddInventoryOpen(true)}
              sx={{ textTransform: 'none', backgroundColor: colorPalette.asseTracOrange, whiteSpace: 'nowrap' }}
            >
              {/* AST-118 (AM-1): renamed; icon, color and position unchanged. */}
              Enter Asset Move
            </Button>
          </>
        )}

        {/* ASS-76: View entries door, beside Add Inventory. Mark hasn't
            decided this log is needed for good -- "he'd like to see it so
            he can better determine if we don't need it" -- but it's on
            the demo path until he says otherwise.
            AST-119 (MH-1): renamed "Move History"; position and style
            unchanged. Only ever shown on manual/Both projects, so beacon
            projects are unaffected (MH-9). */}
        {isManualOrBothProject && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/manual/log?projectId=${currentProject.id}`)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' }}
          >
            Move History
          </Button>
        )}

        {/* AST-88: See Mat Locations -- right of View entries, secondary
            style like it. Decision: shown on manual and Both projects (the
            spec says any mode, but it's manual counts only, so on a
            beacon-only project it would always read zero).
            AST-120 (AC-R1): renamed "Show Asset Counts"; position and style
            unchanged. Never rendered on beacon projects (AC-R10). */}
        {isManualOrBothProject && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setMatLocationsOpen(true)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' }}
          >
            Show Asset Counts
          </Button>
        )}

        {/* AST-125 RB: the Map Areas button is gone from the toolbar. Mapped
            areas are edited from the project's Edit Project screen instead
            (Projects list › Edit). The map still draws them (RB-3), and
            nothing about the stored areas changes (RB-4). */}

        {activeViewTab === 0 && !isManualProject && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }} />

            <FilterChip
              icon={icnAssets}
              label={filterAssetTypes.length > 0 && filterAssetTypes.length < uniqueAssetTypes.length
                ? `Assets (${filterAssetTypes.length})`
                : 'Assets'}
              active={activeFilter === 'type'}
              onClick={(e) => { setFilterAnchorEl(e.currentTarget); setActiveFilter('type'); }}
              aria-label="Filter by asset type"
            />

            <FilterChip
              icon={icnTag}
              label={filterOwners.length > 0 && filterOwners.length < uniqueOwners.length
                ? `Owner (${filterOwners.length})`
                : 'Owner'}
              active={activeFilter === 'owner'}
              onClick={(e) => { setFilterAnchorEl(e.currentTarget); setActiveFilter('owner'); }}
              aria-label="Filter by owner"
            />

            <FilterChip
              icon={icnError}
              label={(filterBatteryIssues || filterOfflineGateways)
                ? `Issues (${(filterBatteryIssues ? 1 : 0) + (filterOfflineGateways ? 1 : 0)})`
                : 'Issues'}
              active={activeFilter === 'issues'}
              onClick={(e) => { setFilterAnchorEl(e.currentTarget); setActiveFilter('issues'); }}
              aria-label="Filter by issue type"
            />

            {/* Multi-select Filter Popover */}
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={() => { setFilterAnchorEl(null); setActiveFilter(null); }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ minWidth: 260, display: 'flex', flexDirection: 'column' }}>
                {/* Checkbox List */}
                <List dense sx={{ maxHeight: 300, overflow: 'auto', py: 1, px: 1 }}>
                  {activeFilter === 'issues' ? (
                    <>
                      {/* Select All for Issues */}
                      <ListItem disablePadding sx={{ mb: `${SPACING.sm}px` }}>
                        <ListItemButton
                          onClick={() => {
                            const allSelected = filterBatteryIssues && filterOfflineGateways;
                            setFilterBatteryIssues(!allSelected);
                            setFilterOfflineGateways(!allSelected);
                          }}
                          dense
                        >
                          <Checkbox
                            checked={filterBatteryIssues && filterOfflineGateways}
                            indeterminate={(filterBatteryIssues || filterOfflineGateways) && !(filterBatteryIssues && filterOfflineGateways)}
                            sx={{ mr: 1 }}
                          />
                          <ListItemText 
                            primary="Select all" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} 
                          />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setFilterBatteryIssues(!filterBatteryIssues)} dense>
                          <Checkbox checked={filterBatteryIssues} sx={{ mr: 1 }} />
                          <ListItemText primary="Battery Issues" primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setFilterOfflineGateways(!filterOfflineGateways)} dense>
                          <Checkbox checked={filterOfflineGateways} sx={{ mr: 1 }} />
                          <ListItemText primary="Offline Gateways" primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      {/* Select All as checkbox */}
                      <ListItem disablePadding sx={{ mb: `${SPACING.sm}px` }}>
                        <ListItemButton
                          onClick={() => {
                            if (activeFilter === 'type') {
                              setFilterAssetTypes(filterAssetTypes.length === 0 ? [...uniqueAssetTypes] : []);
                            } else {
                              setFilterOwners(filterOwners.length === 0 ? [...uniqueOwners] : []);
                            }
                          }}
                          dense
                        >
                          <Checkbox
                            checked={activeFilter === 'type' ? filterAssetTypes.length === 0 : filterOwners.length === 0}
                            indeterminate={
                              activeFilter === 'type'
                                ? filterAssetTypes.length > 0 && filterAssetTypes.length < uniqueAssetTypes.length
                                : filterOwners.length > 0 && filterOwners.length < uniqueOwners.length
                            }
                            sx={{ mr: 1 }}
                          />
                          <ListItemText 
                            primary="Select all" 
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} 
                          />
                        </ListItemButton>
                      </ListItem>
                      
                      {/* Individual items */}
                      {(activeFilter === 'type' ? uniqueAssetTypes : uniqueOwners).map((item) => {
                        const selected = activeFilter === 'type'
                          ? filterAssetTypes.length === 0 || filterAssetTypes.includes(item)
                          : filterOwners.length === 0 || filterOwners.includes(item);
                        return (
                          <ListItem key={item} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                if (activeFilter === 'type') {
                                  const current = filterAssetTypes.length === 0 ? [...uniqueAssetTypes] : [...filterAssetTypes];
                                  setFilterAssetTypes(current.includes(item) ? current.filter(t => t !== item) : [...current, item]);
                                } else {
                                  const current = filterOwners.length === 0 ? [...uniqueOwners] : [...filterOwners];
                                  setFilterOwners(current.includes(item) ? current.filter(o => o !== item) : [...current, item]);
                                }
                              }}
                              dense
                            >
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </>
                  )}
                </List>
                
                {/* Divider before action buttons */}
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      if (activeFilter === 'type') {
                        setFilterAssetTypes([]);
                      } else if (activeFilter === 'owner') {
                        setFilterOwners([]);
                      } else {
                        setFilterBatteryIssues(false);
                        setFilterOfflineGateways(false);
                      }
                    }}
                    sx={{ 
                      textTransform: 'none',
                      color: 'text.secondary',
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setFilterAnchorEl(null);
                      setActiveFilter(null);
                    }}
                    sx={{ 
                      textTransform: 'none',
                      minWidth: 80,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Popover>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Typography sx={{ color: '#A09C9A', fontSize: '14px', fontWeight: 400 }}>
                Overlays
              </Typography>
              <IconButton
                onClick={() => setOverlaysPanelOpen(prev => !prev)}
                aria-label={overlaysPanelOpen ? 'Hide overlays panel' : 'Show overlays panel'}
                sx={{
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '8px',
                  width: 'auto',
                  height: 'auto',
                  '&:hover': { backgroundColor: 'rgba(232,220,218,0.08)' },
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 24, color: '#FFFFFF', lineHeight: 1 }}
                  aria-hidden="true"
                >
                  {overlaysPanelOpen ? 'right_panel_close' : 'right_panel_open'}
                </span>
              </IconButton>
            </Box>
          </>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Main Content Area */}
        <Box 
          className={overlaysPanelOpen ? 'side-sheet-open' : ''}
          sx={{ flexGrow: 1, position: 'relative', overflow: 'auto', minHeight: 0 }}
        >
          {/* Map View (tab 0) */}
          {activeViewTab === 0 && (
            <>
              {/* ASS-81 Part B: manual projects need their point-marker
                  visible (Mark: "it needs to put a project marker on the
                  map then at that lat/long"). Boundary-having projects
                  keep showProjectMarkers=false -- their own page never
                  needed the P/Y hexagon overlaid on top of their own
                  boundary before, and still doesn't. */}
              {/* AST-96: a manual project with mapped elements but no
                  point still gets a map -- it's fitted to the elements. */}
              {isManualProject && !hasValidProjectPoint && !hasAreaMarkers ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Add a latitude and longitude to place this project on the map.{' '}
                    {/* ASS-81 Part B: this page's own inline Edit modal
                        (handleEnterEditMode / :1123) has no location
                        fields at all -- linking to it wouldn't actually
                        let the user add a lat/long. The real location
                        editor is ProjectListPage.jsx's shared
                        ProjectDetailsForm modal, so this links there
                        instead of opening the wrong dialog. */}
                    <MuiLink component="button" onClick={() => navigate('/projects')}>Edit</MuiLink>
                  </Typography>
                </Box>
              ) : (
                <>
                  <AsseTracMap
                    visibleOverlays={visibleOverlays}
                    showProjectMarkers={isManualProject && hasValidProjectPoint}
                    showBoundaryFill={false}
                    areaElementMarkers={areaDots}
                    areaElementShapes={areaShapes}
                    structureBadges={structureBadges}
                  />
                  {/* AST-124 MA: the "Mapped areas" legend is gone (Mark: it
                      duplicates what the map already shows). It was ALSO the
                      only place a failed area load was surfaced, so that
                      warning now goes to the snackbar -- see the effect
                      below -- rather than failing silently. */}
                </>
              )}
              <LoadingOverlay loading={assetsLoading} />

              <MapOverlaysPanel
                open={overlaysPanelOpen}
                onClose={() => setOverlaysPanelOpen(false)}
                visibleOverlays={visibleOverlays}
                onToggleOverlay={handleToggleOverlay}
                onToggleAll={handleToggleAllOverlays}
                projectAreas={projectAreas}
                manualCounts={isManualProject ? { checked: showManualCounts, onToggle: () => setShowManualCounts((v) => !v) } : null}
              />

              {/* Project Inventory Panel - bottom left */}
              <ProjectInventoryPanel assets={viewportAssets} />
              
              {/* Mobile: Floating back button */}
              {isMobile && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<BackIcon />}
                  onClick={handleBackToProjects}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 10,
                    boxShadow: 2,
                  }}
                >
                  Back
                </Button>
              )}
            </>
          )}

          {/* Moves View (tab 1) */}
          {activeViewTab === 1 && (
            <MovesTable projectId={id} customerId={activeCustomer?.id} />
          )}
        </Box>
      </Box>

      {/* Anomaly Drawer */}
      <AnomalyDrawer
        open={anomalyDrawerOpen}
        onClose={() => setAnomalyDrawerOpen(false)}
        assets={projectAssetList}
        assetTypes={[]}
      />

      {/* ASS-75: Add Inventory dialog. Save dispatches createEntryAction
          directly and does not refetch -- see AddInventoryDialog.jsx's
          header comment for why a refetch here would be a regression. */}
      <AddInventoryDialog
        open={addInventoryOpen}
        onClose={() => setAddInventoryOpen(false)}
        project={currentProject}
        onSaved={(message) => setSnackbar({ open: true, message, severity: 'success' })}
      />

      {isManualOrBothProject && (
        <MatLocationsDialog
          open={matLocationsOpen}
          onClose={() => setMatLocationsOpen(false)}
          projectId={currentProject?.id}
          projectName={currentProject?.name}
          entries={projectEntries || []}
        />
      )}

      {isManualProject && (
        <MapAreasDialog
          open={mapAreasOpen}
          projectId={currentProject?.id}
          onClose={() => setMapAreasOpen(false)}
          onSaved={(res) => {
            setAreaElementsVersion((v) => v + 1);
            const n = res?.count ?? 0;
            const c = res?.changes || {};
            const parts = [c.created && `${c.created} added`, c.retired && `${c.retired} removed`].filter(Boolean);
            setSnackbar({ open: true, message: `Areas saved: ${n} element${n === 1 ? '' : 's'}${parts.length ? ` (${parts.join(', ')})` : ''}.`, severity: 'success' });
          }}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.action ? 8000 : 4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          action={
            snackbar.action === 'mapAreas' ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSnackbar({ ...snackbar, open: false });
                  setMapAreasOpen(true);
                }}
              >
                Map areas
              </Button>
            ) : snackbar.action === 'addInventory' ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSnackbar({ ...snackbar, open: false });
                  setAddInventoryOpen(true);
                }}
              >
                Add inventory
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectPage;
